import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Star, Tag, Trash2 } from "lucide-react";

const EMPTY = {
  code: "", label: "", discount_type: "percent", value: 10,
  min_order: 0, usage_limit: "", expires_at: "", active: true, is_primary: false,
};

export const OffersTab = () => {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/admin/coupons").then(({ data }) => setOffers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const payload = () => ({
    ...form,
    code: form.code.trim().toUpperCase(),
    value: parseFloat(form.value) || 0,
    min_order: parseFloat(form.min_order) || 0,
    usage_limit: form.usage_limit === "" ? null : parseInt(form.usage_limit, 10),
  });

  const save = async () => {
    try {
      if (editingId) await api.put(`/admin/coupons/${editingId}`, payload());
      else await api.post("/admin/coupons", payload());
      toast.success(editingId ? "Offer updated" : "Offer created");
      setForm(EMPTY); setEditingId(null); setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggle = async (o, active) => {
    try {
      await api.put(`/admin/coupons/${o.id}`, { ...o, active });
      toast.success(active ? `${o.code} is live` : `${o.code} switched off`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const makePrimary = async (o) => {
    try {
      await api.put(`/admin/coupons/${o.id}`, { ...o, is_primary: !o.is_primary, active: true });
      toast.success(o.is_primary ? `${o.code} removed from the promo bar` : `${o.code} now shows on the promo bar`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/admin/coupons/${id}`);
    load();
    toast.success("Deleted");
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl">Shop Offers</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Discount codes buyers type at checkout. Switch one on for Diwali or a wellness week and
            off again afterwards — GST is charged on the discounted amount. The starred offer is the
            one advertised on the sticky promo bar and on the featured products carousel; switching
            an offer off removes it from the site and it stops working at checkout at once.
          </p>
        </div>
        <Button
          onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }}
          className="bg-primary text-primary-foreground btn-pill"
          data-testid="offer-add"
        >
          <Plus size={14} className="mr-1"/> New Offer
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {offers.map((o) => (
          <Card key={o.id} className="border-border" data-testid={`offer-card-${o.code}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Tag size={14} className="text-secondary"/>
                    <span className="font-display text-xl tracking-wide">{o.code}</span>
                    <button
                      onClick={() => makePrimary(o)}
                      title={o.is_primary ? "Showing on the promo bar" : "Show this one on the promo bar"}
                      data-testid={`offer-primary-${o.code}`}
                      className={o.is_primary ? "text-secondary" : "text-muted-foreground/50 hover:text-secondary"}
                    >
                      <Star size={14} fill={o.is_primary ? "currentColor" : "none"}/>
                    </button>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{o.label || "—"}</div>
                </div>
                <Switch checked={o.active} onCheckedChange={(v) => toggle(o, v)} data-testid={`offer-switch-${o.code}`}/>
              </div>
              <div className="font-display text-2xl mt-3">
                {o.discount_type === "percent" ? `${o.value}% off` : `₹${o.value} off`}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {o.min_order > 0 ? `Carts over ₹${o.min_order}` : "Any cart size"}
                {o.expires_at ? ` · until ${o.expires_at}` : ""}
              </div>
              <div className="flex items-center justify-between mt-4">
                <Badge variant="outline" className="text-[10px]">
                  Used {o.used_count || 0}{o.usage_limit ? ` / ${o.usage_limit}` : ""}
                </Badge>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setForm({ ...EMPTY, ...o, usage_limit: o.usage_limit ?? "", expires_at: o.expires_at || "" });
                      setEditingId(o.id);
                      setOpen(true);
                    }}
                    className="text-xs text-primary hover:underline"
                    data-testid={`offer-edit-${o.code}`}
                  >
                    Edit
                  </button>
                  <button onClick={() => remove(o.id)} className="text-destructive" data-testid={`offer-del-${o.code}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {offers.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="offers-empty">
            No offers yet. Create one and share the code on WhatsApp or Instagram.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditingId(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit offer" : "New offer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code buyers type</Label>
              <Input value={form.code} onChange={(e)=>setForm({...form, code:e.target.value.toUpperCase()})} placeholder="DIWALI25" data-testid="offer-code"/>
            </div>
            <div>
              <Label>Name shown on the cart</Label>
              <Input value={form.label} onChange={(e)=>setForm({...form, label:e.target.value})} placeholder="Diwali wellness week" data-testid="offer-label"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount type</Label>
                <Select value={form.discount_type} onValueChange={(v)=>setForm({...form, discount_type:v})}>
                  <SelectTrigger data-testid="offer-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent off</SelectItem>
                    <SelectItem value="flat">Flat ₹ off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.discount_type === "percent" ? "Percent (1-90)" : "Amount (₹)"}</Label>
                <Input type="number" value={form.value} onChange={(e)=>setForm({...form, value:e.target.value})} data-testid="offer-value"/>
              </div>
              <div>
                <Label>Minimum cart (₹)</Label>
                <Input type="number" value={form.min_order} onChange={(e)=>setForm({...form, min_order:e.target.value})} data-testid="offer-min"/>
              </div>
              <div>
                <Label>Total uses (blank = unlimited)</Label>
                <Input type="number" value={form.usage_limit} onChange={(e)=>setForm({...form, usage_limit:e.target.value})} data-testid="offer-limit"/>
              </div>
              <div className="col-span-2">
                <Label>Runs until (optional)</Label>
                <Input type="date" value={form.expires_at} onChange={(e)=>setForm({...form, expires_at:e.target.value})} data-testid="offer-expires"/>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm">Live right now</span>
              <Switch checked={form.active} onCheckedChange={(v)=>setForm({...form, active:v})} data-testid="offer-active"/>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm">
                Show on the sticky promo bar
                <span className="block text-xs text-muted-foreground mt-0.5">
                  The whole site advertises this one code. Only one offer can hold it.
                </span>
              </span>
              <Switch checked={!!form.is_primary} onCheckedChange={(v)=>setForm({...form, is_primary:v})} data-testid="offer-primary-switch"/>
            </div>
          </div>
          <Button onClick={save} className="bg-primary text-primary-foreground mt-4" data-testid="offer-save">
            {editingId ? "Update offer" : "Create offer"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OffersTab;
